import { useState } from 'react';
import { useApp } from '../App';
import api from '../utils/api';

const EMPTY = { name:'', email:'', address:'', phone:'', contact_person:'', notes:'' };
const COLORS = [
  ['#FFF0E8','#C4621A'],['#E8F4FF','#1A4A80'],['#F0FAFF','#0D6B8C'],
  ['#F0FFF4','#0D6B44'],['#F5F0FF','#4A1A80'],['#FFF8E8','#8A6200'],
];

export default function Clients() {
  const { user, clients, setClients, toast, setPage } = useApp();
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [delConfirm, setDelConfirm] = useState(null);

  const filtered = clients.filter(c =>
    !search || [c.name,c.email,c.contact_person,c.address].some(v=>v&&v.toLowerCase().includes(search.toLowerCase()))
  );

  const openAdd  = ()  => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name:c.name,email:c.email,address:c.address||'',phone:c.phone||'',contact_person:c.contact_person||'',notes:c.notes||'' }); setModal(true); };

  const save = async () => {
    if (!form.name||!form.email) { toast('Name and email are required','error'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        setClients(clients.map(c=>c.id===editing.id?{...c,...form}:c));
        toast('Client updated','success');
      } else {
        const created = await api.post('/clients', form);
        setClients([...clients, { ...form, id:created.id }]);
        toast('Client added: '+form.name,'success');
      }
      setModal(false);
    } catch(e) { toast(e.message,'error'); }
    finally { setSaving(false); }
  };

  const doDelete = async (c) => {
    try {
      await api.delete(`/clients/${c.id}`);
      setClients(clients.filter(x=>x.id!==c.id));
      setDelConfirm(null);
      toast('Client removed','success');
    } catch(e) { toast(e.message,'error'); }
  };

  const initials = name => name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

  return (
    <div className="animate-fade-up">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Directory</div>
          <div className="page-title">Clients</div>
          <div className="page-sub">{clients.length} saved client{clients.length!==1?'s':''}</div>
        </div>
        <button className="btn btn-copper" onClick={openAdd}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
          Add client
        </button>
      </div>

      <div style={{ marginBottom:20 }}>
        <div className="search-wrap">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input className="search-input" placeholder="Search clients…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
      </div>

      {filtered.length===0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👥</div>
          <div className="empty-state-title">{search?'No clients match':'No clients yet'}</div>
          <div className="empty-state-sub">{search?'Try a different search term':'Add your first client to get started'}</div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(295px,1fr))', gap:16 }} className="stagger">
          {filtered.map((c,i)=>{
            const [bg,fg] = COLORS[i%COLORS.length];
            return (
              <div key={c.id} className="card animate-fade-up"
                style={{ padding:'20px 22px', transition:'all 0.2s var(--ease-out)' }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:14 }}>
                  <div style={{ width:42,height:42,borderRadius:12,background:bg,color:fg,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:14,flexShrink:0,fontFamily:'var(--font-display)' }}>
                    {initials(c.name)}
                  </div>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontWeight:700,fontSize:14,letterSpacing:'-0.01em',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{c.name}</div>
                    {c.contact_person && <div style={{ fontSize:11,color:'var(--muted)',marginTop:1 }}>Contact: {c.contact_person}</div>}
                  </div>
                </div>
                <div style={{ display:'flex',flexDirection:'column',gap:5,fontSize:12,color:'var(--mid)' }}>
                  <div style={{ display:'flex',gap:8,alignItems:'center' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0,color:'var(--ghost)' }}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    <a href={`mailto:${c.email}`} style={{ color:'var(--navy)',textDecoration:'none',fontWeight:500 }}>{c.email}</a>
                  </div>
                  {c.phone && <div style={{ display:'flex',gap:8,alignItems:'center' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0,color:'var(--ghost)' }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.32 2 2 0 0 1 3.57 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.54a16 16 0 0 0 5.55 5.55l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z"/></svg>{c.phone}</div>}
                  {c.address && <div style={{ display:'flex',gap:8,alignItems:'flex-start' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0,color:'var(--ghost)',marginTop:1 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg><span>{c.address}</span></div>}
                </div>
                <div style={{ display:'flex',gap:8,marginTop:16,paddingTop:14,borderTop:'1px solid rgba(0,0,0,0.06)' }}>
                  <button className="btn btn-copper btn-sm" style={{ flex:1,justifyContent:'center' }} onClick={()=>setPage('newquote')}>New quote</button>
                  <button className="btn btn-outline btn-sm" onClick={()=>openEdit(c)}>Edit</button>
                  {user.role==='admin' && <button className="btn btn-danger btn-sm" onClick={()=>setDelConfirm(c)}>✕</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{editing?'Edit client':'Add new client'}</div>
              <div className="modal-subtitle">{editing?editing.name:'Fill in the client details below'}</div>
            </div>
            <div className="modal-body">
              <div className="field-row">
                <div className="field"><label className="label">Company / Name <span className="req">*</span></label><input className="input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Acme Corp" autoFocus/></div>
                <div className="field"><label className="label">Contact person</label><input className="input" value={form.contact_person} onChange={e=>setForm(f=>({...f,contact_person:e.target.value}))} placeholder="John Smith"/></div>
              </div>
              <div className="field-row">
                <div className="field"><label className="label">Email <span className="req">*</span></label><input className="input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="contact@company.com"/></div>
                <div className="field"><label className="label">Phone</label><input className="input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+1 868 555-0100"/></div>
              </div>
              <div className="field"><label className="label">Address</label><input className="input" value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} placeholder="Street, City, Trinidad"/></div>
              <div className="field"><label className="label">Notes</label><textarea className="textarea" style={{ minHeight:70 }} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Any special notes…"/></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-copper" onClick={save} disabled={saving}>{saving?'Saving…':editing?'Save changes':'Add client'}</button>
            </div>
          </div>
        </div>
      )}

      {delConfirm && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setDelConfirm(null)}>
          <div className="modal" style={{ width:380 }}>
            <div className="modal-body" style={{ paddingTop:28 }}>
              <div style={{ width:48,height:48,borderRadius:12,background:'#FEECEC',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:14 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E03B3B" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              </div>
              <div style={{ fontFamily:'var(--font-display)',fontSize:18,fontWeight:700,marginBottom:8 }}>Remove client?</div>
              <p style={{ fontSize:13,color:'var(--mid)',lineHeight:1.6 }}>This will remove <strong>{delConfirm.name}</strong>. Existing quotes will be unaffected.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setDelConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={()=>doDelete(delConfirm)}>Remove client</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
